import React from 'react';
import { Form, Select, Typography, Checkbox, Button } from 'antd';
import { FormInstance } from 'antd/es/form';
// Api
import { useQuery } from '@apollo/client';
import { GET_INFLUENCER_CATEGORIES } from 'api/influencerCategory/queries';
import { GetInfluencerCategories } from 'api/influencerCategory/types/GetInfluencerCategories';
// Types
import { StoreGender, UserRole } from 'api/graphql-global-types';
// Helpers
import { GENDER_OPTIONS } from 'helpers/campaignEditModal';
// Components
import SportsSelect from 'components/common/SportsSelect/SportsSelect';
import LocationSelect from 'components/common/LocationSelect/LocationSelect';
import HashtagSelect from 'components/common/HashtagSelect/HashtagSelect';
// Utils
import { CustomCampaignTalentAgeRange } from 'helpers/customCampaign';
import {
  AGE_RANGE_OPTIONS,
  TALENT_TYPE_OPTIONS,
  TalentType,
  getTalentTypeFromRoles,
} from 'helpers/customCampaignConstants';
// Styles
import styles from './CustomCampaignTalentFilters.module.scss';

const { Text } = Typography;
const { Option } = Select;

type CustomCampaignTalentFiltersProps = {
  form: FormInstance<any>;
};

const CustomCampaignTalentFilters = ({
  form,
}: CustomCampaignTalentFiltersProps): JSX.Element => {
  const { data: categoriesData } = useQuery<GetInfluencerCategories>(
    GET_INFLUENCER_CATEGORIES
  );
  const influencerCategories = categoriesData?.getInfluencerCategories ?? [];

  const handleTalentTypeChange = (type: TalentType) => {
    const option = TALENT_TYPE_OPTIONS.find((o) => o.value === type);
    if (option) {
      form.setFieldsValue({ userRole: [...option.roles] });
    }
  };

  return (
    <div className={styles.filterGroup}>
      <h4 className={styles.sectionTitle}>Talent Filters</h4>

      <div>
        <Text className={styles.fieldLabel}>Talent Type</Text>
        {/* shouldUpdate re-renders the buttons when userRole changes so the
          active state stays in sync with the (prepopulated) form value. */}
        <Form.Item
          shouldUpdate={(prev, cur) => prev.userRole !== cur.userRole}
          noStyle
        >
          {() => {
            const roles = (form.getFieldValue('userRole') || []) as UserRole[];
            const activeType = getTalentTypeFromRoles(roles);
            return (
              <div className={styles.talentTypeButtons}>
                {TALENT_TYPE_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    type={activeType === option.value ? 'primary' : 'default'}
                    onClick={() => handleTalentTypeChange(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            );
          }}
        </Form.Item>
        {/* userRole is the persisted field driven by the buttons above */}
        <Form.Item name="userRole" hidden>
          <Select mode="multiple" />
        </Form.Item>
      </div>

      <div className={styles.row}>
        <SportsSelect
          name="sports"
          label="Sports"
          placeholder="Select sports"
          initialValue={form.getFieldValue('sports')}
        />

        <Form.Item
          name="genders"
          label={<Text>Gender</Text>}
          getValueProps={(genders: StoreGender[] | undefined) => ({
            value: Object.values(StoreGender).every((gender) =>
              genders?.includes(gender)
            )
              ? ''
              : genders?.[0],
          })}
          getValueFromEvent={(gender: StoreGender | '' | undefined) =>
            gender === '' ? Object.values(StoreGender) : gender ? [gender] : []
          }
        >
          <Select id="genders" placeholder="Select gender" allowClear>
            {GENDER_OPTIONS.map((item) => (
              <Option value={`${item.value}`} key={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="ageRanges"
          label={<Text>Age Range</Text>}
          getValueProps={(
            ageRanges: CustomCampaignTalentAgeRange[] | null | undefined
          ) => ({
            value: AGE_RANGE_OPTIONS.filter((option) =>
              (ageRanges || []).some(
                (range) =>
                  range.minAge === option.minAge &&
                  range.maxAge === option.maxAge
              )
            ).map((option) => option.value),
          })}
          getValueFromEvent={(values: string[]) =>
            AGE_RANGE_OPTIONS.filter((option) =>
              values.includes(option.value)
            ).map((option) => ({
              minAge: option.minAge,
              maxAge: option.maxAge,
            }))
          }
        >
          <Select mode="multiple" placeholder="Select Age Range" allowClear>
            {AGE_RANGE_OPTIONS.map((option) => (
              <Option value={option.value} key={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <div className={styles.row}>
        <Form.Item
          name="influencerCategoryIds"
          label={<Text>Influencer Categories</Text>}
        >
          <Select
            mode="multiple"
            placeholder="Select categories"
            allowClear
            optionFilterProp="children"
          >
            {influencerCategories.map((category) => (
              <Option value={category.id} key={category.id}>
                {category.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <div className={styles.row}>
        <Form.Item shouldUpdate noStyle>
          {() => (
            <Form.Item label={<Text>Location</Text>}>
              <LocationSelect
                id="locations"
                value={form.getFieldValue('locations') || []}
                onChange={(locations) => form.setFieldsValue({ locations })}
                placeholder="Search country, state, or city"
              />
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item shouldUpdate noStyle>
          {() => (
            <Form.Item label={<Text>Exclude Location</Text>}>
              <LocationSelect
                id="excludedLocations"
                value={form.getFieldValue('excludedLocations') || []}
                onChange={(excludedLocations) =>
                  form.setFieldsValue({ excludedLocations })
                }
                placeholder="Search country, state, or city"
              />
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item shouldUpdate noStyle>
          {() => (
            <Form.Item label={<Text>Hometown</Text>}>
              <LocationSelect
                id="hometownLocations"
                value={form.getFieldValue('hometownLocations') || []}
                onChange={(hometownLocations) =>
                  form.setFieldsValue({ hometownLocations })
                }
                placeholder="Search country, state, or city"
              />
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item shouldUpdate noStyle>
          {() => (
            <Form.Item label={<Text>Tags</Text>}>
              <HashtagSelect
                name="searchHashtags"
                value={form.getFieldValue('searchHashtags') || []}
                onChange={(searchHashtags) =>
                  form.setFieldsValue({ searchHashtags })
                }
                placeholder="Search tags"
              />
            </Form.Item>
          )}
        </Form.Item>
      </div>

      <div className={styles.checkboxGroup}>
        <Form.Item
          name="excludePreviouslyWorkedWith"
          valuePropName="checked"
          noStyle
        >
          <Checkbox>Exclude talent I&apos;ve previously worked with</Checkbox>
        </Form.Item>
        <Form.Item
          name="excludePreviouslyDeclined"
          valuePropName="checked"
          noStyle
        >
          <Checkbox>Exclude talent I&apos;ve previously declined</Checkbox>
        </Form.Item>
      </div>
    </div>
  );
};

export default CustomCampaignTalentFilters;
